import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightning, Lock, ArrowRight, Crown, Star, Warning, X, ChartLine, Robot, BookOpen, Users, Trophy, Handshake, ChartBar, Sparkle } from '@phosphor-icons/react';
import axios from 'axios';
import { useMonetizationMode } from '../hooks/useEntitlement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * UpgradeBanner — Full-width banner shown when user is near or at quota limit.
 * Hidden in monetization disabled/soft modes.
 */
export function UpgradeBanner({ used, limit, featureName, plan = 'Plus', className = '' }) {
  const navigate = useNavigate();
  const { show_paywalls } = useMonetizationMode();
  if (!show_paywalls) return null;
  if (!limit || limit >= 9999) return null;
  const pct = Math.min(100, (used / limit) * 100);
  if (pct < 70) return null; // Only show when 70%+ used

  const isExhausted = used >= limit;
  const accent = isExhausted ? '#EF4444' : '#F59E0B';

  return (
    <div className={`rounded-lg border px-4 py-3 mb-4 ${className}`}
      style={{ backgroundColor: `${accent}08`, borderColor: `${accent}25` }}
      data-testid="upgrade-banner">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {isExhausted
            ? <Warning size={18} weight="fill" style={{ color: accent }} />
            : <Lightning size={18} weight="duotone" style={{ color: accent }} />}
          <div className="min-w-0">
            <p className="text-[13px] font-medium" style={{ color: accent }}>
              {isExhausted
                ? `${featureName} limit reached`
                : `${used}/${limit} ${featureName} used this month`}
            </p>
            <p className="text-[11px] text-[#555]">
              {isExhausted
                ? `Upgrade to ${plan} for more`
                : `${limit - used} remaining. Upgrade to ${plan} for more.`}
            </p>
          </div>
        </div>
        <button onClick={() => navigate('/pricing')}
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors"
          style={{ backgroundColor: `${accent}15`, color: accent }}
          data-testid="upgrade-banner-btn">
          Upgrade <ArrowRight size={12} />
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-1 bg-white/[0.04] rounded-full mt-2.5 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: accent }} />
      </div>
    </div>
  );
}

/**
 * LockedFeature — Overlay for features that require a higher plan.
 * Usage: <LockedFeature feature="Advanced Analytics" plan="Plus">{children}</LockedFeature>
 */
export function LockedFeature({ feature, plan = 'Plus', children, allowed = false }) {
  const navigate = useNavigate();
  const { show_paywalls } = useMonetizationMode();
  // In disabled/soft mode, never lock
  if (allowed || !show_paywalls) return children;

  return (
    <div className="relative" data-testid={`locked-${feature.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="pointer-events-none select-none opacity-30 blur-[1px]">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-[#0c0c14]/95 border border-white/[0.08] rounded-xl px-6 py-5 text-center max-w-xs backdrop-blur-sm">
          <Lock size={28} weight="duotone" className="text-[#F59E0B] mx-auto mb-2" />
          <p className="text-white font-semibold text-[14px] mb-1">{feature}</p>
          <p className="text-[12px] text-[#555] mb-4">Available on {plan} plan and above</p>
          <button onClick={() => navigate('/pricing')}
            className="w-full py-2 rounded-lg bg-[#F59E0B] text-black text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#E8A308] transition-colors"
            data-testid="locked-upgrade-btn">
            <Crown size={14} weight="fill" /> Upgrade to {plan}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * UpgradeChip — Small inline chip for individual locked features.
 * Usage: <UpgradeChip plan="Pro" />
 */
export function UpgradeChip({ plan = 'Plus', label }) {
  const navigate = useNavigate();
  const { show_paywalls } = useMonetizationMode();
  if (!show_paywalls) return null;
  return (
    <button onClick={() => navigate('/pricing')}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-semibold hover:bg-[#F59E0B]/20 transition-colors"
      data-testid="upgrade-chip">
      <Crown size={10} weight="fill" /> {label || plan}
    </button>
  );
}

/**
 * QuotaIndicator — Minimal quota display for headers/toolbars.
 * Usage: <QuotaIndicator used={5} limit={20} label="AI" />
 */
export function QuotaIndicator({ used, limit, label = 'AI' }) {
  const { mode } = useMonetizationMode();
  // In disabled/soft mode, always show "Unlimited"
  if (mode !== 'enabled' || !limit || limit >= 9999) return (
    <span className="text-[10px] text-[#333] flex items-center gap-1" data-testid="quota-indicator">
      <Star size={10} weight="fill" className="text-[#F59E0B]" /> Unlimited
    </span>
  );
  const pct = Math.min(100, (used / limit) * 100);
  const color = pct >= 90 ? '#EF4444' : pct >= 70 ? '#F59E0B' : '#10B981';
  return (
    <span className="text-[10px] flex items-center gap-1.5 tabular-nums" style={{ color }}
      data-testid="quota-indicator">
      {used}/{limit} {label}
    </span>
  );
}


const NUDGE_ICONS = {
  'chart-line': ChartLine,
  'robot': Robot,
  'book-open': BookOpen,
  'users': Users,
  'trophy': Trophy,
  'handshake': Handshake,
  'chart-bar': ChartBar,
  'crown': Crown,
};

const NUDGE_ACCENTS = {
  'chart-line': '#00F0FF',
  'robot': '#9D4CDD',
  'book-open': '#10B981',
  'users': '#39FF14',
  'trophy': '#FACC15',
  'handshake': '#F97316',
  'chart-bar': '#00F0FF',
  'crown': '#F59E0B',
};

/**
 * SmartNudge — Contextual upgrade prompt triggered by user behavior milestones.
 * Props: nudge object from /api/monetization/my/nudges
 */
export function SmartNudge({ nudge, onDismiss, variant = 'card' }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (!nudge || dismissed) return null;

  const Icon = NUDGE_ICONS[nudge.icon] || Sparkle;
  const accent = NUDGE_ACCENTS[nudge.icon] || '#F59E0B';

  const handleDismiss = async () => {
    setDismissed(true);
    try {
      await axios.post(`${API_URL}/api/monetization/my/nudges/${nudge.nudge_id}/dismiss`, {}, { withCredentials: true });
    } catch { /* silent */ }
    onDismiss?.(nudge.nudge_id);
  };

  if (variant === 'toast') {
    return (
      <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-500"
        data-testid={`smart-nudge-${nudge.nudge_id}`}>
        <div className="rounded-2xl border px-4 py-3.5 backdrop-blur-xl shadow-2xl"
          style={{ backgroundColor: '#0c0c14ee', borderColor: `${accent}30` }}>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${accent}18` }}>
              <Icon size={18} weight="duotone" style={{ color: accent }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-[13px] font-semibold text-white">{nudge.title}</p>
                <button onClick={handleDismiss} className="text-[#555] hover:text-white transition-colors p-0.5"
                  data-testid="nudge-dismiss-btn">
                  <X size={14} />
                </button>
              </div>
              <p className="text-[11px] text-[#888] mt-0.5 leading-relaxed">{nudge.message}</p>
              <button onClick={() => navigate('/pricing')}
                className="mt-2.5 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:brightness-110"
                style={{ backgroundColor: `${accent}20`, color: accent }}
                data-testid="nudge-cta-btn">
                {nudge.cta} <ArrowRight size={11} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: inline card variant
  return (
    <div className="rounded-xl border px-4 py-3.5 mb-4 transition-all"
      style={{ backgroundColor: `${accent}06`, borderColor: `${accent}18` }}
      data-testid={`smart-nudge-${nudge.nudge_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: `${accent}15` }}>
          <Icon size={18} weight="duotone" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-semibold text-white">{nudge.title}</p>
            <button onClick={handleDismiss} className="text-[#444] hover:text-white transition-colors p-0.5"
              data-testid="nudge-dismiss-btn">
              <X size={12} />
            </button>
          </div>
          <p className="text-[11px] text-[#777] mt-0.5 leading-relaxed">{nudge.message}</p>
          <div className="flex items-center gap-2 mt-2.5">
            <button onClick={() => navigate('/pricing')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:brightness-110"
              style={{ backgroundColor: `${accent}20`, color: accent }}
              data-testid="nudge-cta-btn">
              {nudge.cta} <ArrowRight size={11} />
            </button>
            <span className="text-[10px] text-[#555]">
              {nudge.milestone_value}/{nudge.threshold} milestone
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SmartNudgeStack — Renders a stack of nudge cards, limited to maxShow.
 */
export function SmartNudgeStack({ nudges, maxShow = 2, variant = 'card', onDismiss }) {
  if (!nudges?.length) return null;
  const visible = nudges.slice(0, maxShow);
  return (
    <div data-testid="smart-nudge-stack">
      {visible.map(n => (
        <SmartNudge key={n.nudge_id} nudge={n} variant={variant} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
